import { Button, Col, Modal, Row, Spin, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { IDictionary } from '~utilities/IDictionary';
import './loan-payment-abas.less';
import { currencyFormatter } from '~utilities/formatters';
import { Key, TableRowSelection } from 'antd/lib/table/interface';
import { Dispatch } from 'redux';
import IWarehouseLoanTransaction from '~Api/Warehouse/IWarehouseLoanTransaction';
import { loanPaymentAbasUnallocatedWarehouseLoanTransactionsSelector } from './selectors';
import {
    loanPaymentAbasAddAction,
    loanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction,
} from './actions';
import ICheckboxProps from '~utilities/ICheckboxProps';

export default function AddModal(props: {
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { onCancel, isOpen } = props;

    const [ selectedRowKeys, setSelectedRowKeys ] = useState<Key[]>(null);

    const unallocatedTransactions: IDictionary<IWarehouseLoanTransaction> = useSelector(loanPaymentAbasUnallocatedWarehouseLoanTransactionsSelector);

    const actuallySelectedRowKeys: string[] = selectedRowKeys as string[] || _.map(unallocatedTransactions, (unallocatedTransaction: IWarehouseLoanTransaction): string => unallocatedTransaction.uuid);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!unallocatedTransactions) {
            dispatch(loanPaymentAbasUnallocatedWarehouseLoanTransactionsListAction());
        }
    }, [
        dispatch,
        unallocatedTransactions,
    ]);

    const onClickOk: () => void = useCallback(() => {
        dispatch(loanPaymentAbasAddAction(actuallySelectedRowKeys));
        onCancel();
    }, [
        actuallySelectedRowKeys,
        dispatch,
        onCancel,
    ]);

    const onChangeSelectedRows: (rowKeys: Key[]) => void = useCallback((rowKeys: Key[]) => {
        setSelectedRowKeys(rowKeys);
    }, []);

    const getCheckboxProps: (row: IWarehouseLoanTransaction) => ICheckboxProps = useCallback((row: IWarehouseLoanTransaction) => ({
        className: null,
        disabled: false,
        key: row.loanTransactionUuid,
    }), []);

    if (!unallocatedTransactions) {
        return (
            <Modal
                okText='Add'
                okButtonProps={{ disabled: true }}
                open={isOpen}
                title='Add Loan Payment ABA'
                width={800}
                wrapClassName='loan-payment-abas-add-modal'
            >
                <Spin />
            </Modal>
        );
    }

    const rowSelection: TableRowSelection<IWarehouseLoanTransaction> = {
        getCheckboxProps,
        onChange: onChangeSelectedRows,
        selectedRowKeys: actuallySelectedRowKeys,
    };

    const columns: ColumnsType<IWarehouseLoanTransaction> = [
        {
            dataIndex: 'transactionTime',
            defaultSortOrder: 'descend',
            render: (transactionTime: string) => dayjs(transactionTime).format('DD/MM/YYYY'),
            sorter: (a: IWarehouseLoanTransaction, b: IWarehouseLoanTransaction) => dayjs(a.transactionTime) > dayjs(b.transactionTime) ? 1 : -1,
            title: 'Date',
            width: 200,
        },
        {
            dataIndex: 'loanCode',
            render: (loanCode: string, row: IWarehouseLoanTransaction): ReactNode => <Link to={`/loans/${row.loanUuid}`}>{loanCode}</Link>,
            title: 'Loan',
        },
        {
            dataIndex: 'amountPrepaidInterest',
            render: (amountPrepaidInterest: number) => currencyFormatter.format(amountPrepaidInterest * -1),
            title: 'Interest',
            width: 200,
        },
    ];

    const total: number = _.reduce(
        actuallySelectedRowKeys,
        (sum: number, selectedRowKey: string): number => sum + (unallocatedTransactions[selectedRowKey].amountPrepaidInterest * -1),
        0,
    );

    const footer: JSX.Element = (
        <Row align='middle' justify='space-between'>
            <Col>
                <Row style={{ gap: '66px' }}>
                    <Row align='middle' style={{ display: 'flex', gap: '8px' }}>
                        <Col>
                            <Typography.Text>Total Count:</Typography.Text>
                        </Col>
                        <Col>
                            <Typography.Text>{actuallySelectedRowKeys.length}</Typography.Text>
                        </Col>
                    </Row>
                    <Row align='middle' style={{ display: 'flex', gap: '8px' }}>
                        <Col>
                            <Typography.Text>Total Amount:</Typography.Text>
                        </Col>
                        <Col>
                            <Typography.Text>{currencyFormatter.format(total)}</Typography.Text>
                        </Col>
                    </Row>
                </Row>
            </Col>
            <Col>
                <Button key='cancel' type='text' onClick={onCancel}>Cancel</Button>
                <Button
                    disabled={!actuallySelectedRowKeys.length}
                    key='submit'
                    type='primary'
                    onClick={onClickOk}
                >Add</Button>
            </Col>
        </Row>
    );

    return (
        <Modal
            footer={footer}
            onCancel={onCancel}
            open={isOpen}
            title='Add Loan Payment ABA'
            width={800}
            wrapClassName='loan-payment-abas-add-modal'
        >
            <Table
                columns={columns}
                dataSource={_.values(unallocatedTransactions)}
                pagination={false}
                rowKey='uuid'
                rowSelection={rowSelection}
                scroll={{ y: 600 }}
                size='middle'
            />
        </Modal>
    );
}
